import clsx from "clsx";
import { Layers, Plus, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearch } from "wouter";
import { basisLabel } from "../../shared/money";
import { plural } from "../../shared/words";
import { useConfirm } from "../../web/components/Confirm";
import { SyncBadge } from "../../web/components/SyncBadge";
import { Badge, ErrorNotice, ListWarning, Notice, PageHeader, Panel } from "../../web/components/ui";
import { useMoney } from "../../web/lib/money";
import { rowOpen } from "../../web/lib/rowOpen";
import { can, itemTax, shownCost, usePriceListSaver, whyNot } from "./data";
import { describePricingShort } from "./describe";
import { itemSearchText, type MaterialRow, type PriceListData, type PriceLock, priceLock, yearStart } from "./meta";
import { LockMark } from "./PriceLock";
import type { NameTagConfig } from "./names";
import { ServiceM8Name } from "./NameTags";
import { estimatedCost, isPlain, type NeedsCheck, partItem } from "./pricing";
import { type SetupGaps, setupGaps } from "./setup";
import { assessItem, type ItemAssessment, itemContext, type ItemProblem, type ItemProblemId, pricesDueDetail, tintsRow } from "./attention";
import { ListToolbar, matchesSearch, NeedsAttention } from "./ListToolbar";
import { usePriceListWindows } from "./windows";

type Filter = "active" | "unused" | "attention" | "unknown" | "lockedChanged" | "locked" | "changed" | "check" | "costUpdate" | "pricesDue" | "names" | "low" | "partLoss" | "noNumber" | "dupNumber" | "dupName" | "noSupplier" | "noCost" | "typed" | "overrides" | "appOnly" | "archived" | "all";
const FILTERS: { id: Filter; label: string }[] = [
    { id: "active", label: "Active" },
    // Needs attention: what the list flags (rowTone), in the same words as Home's alerts.
    { id: "attention", label: "Anything needing attention" },
    { id: "unknown", label: "Unknown items" },
    { id: "lockedChanged", label: "Locked price changed in ServiceM8" },
    { id: "changed", label: "Changed in ServiceM8" },
    { id: "check", label: "Price to update" },
    { id: "costUpdate", label: "Cost to update" },
    { id: "pricesDue", label: "Supplier price to check" },
    { id: "names", label: "Name to update (group tags)" },
    { id: "low", label: "Making too little" },
    { id: "partLoss", label: "Part at or below cost" },
    { id: "noNumber", label: "No item number" },
    { id: "dupNumber", label: "Shared item number" },
    { id: "dupName", label: "Shared name" },
    { id: "noSupplier", label: "No supplier price" },
    { id: "noCost", label: "No cost" },
    { id: "typed", label: "Price typed in (no rule)" },
    { id: "overrides", label: "Overriding their group's price" },
    { id: "locked", label: "Price locked" },
    // Only once jobs and job lines are read in full (data.lastUsed): see USE_GROUP.
    { id: "unused", label: "Not used in 12 months" },
    { id: "appOnly", label: "Only in this app" },
    { id: "archived", label: "Archived" },
    { id: "all", label: "All" },
];
/** Rows drawn at a time (the rest come with "Show more"), so big price lists stay quick. */
const PAGE = 200;

/** The materials list: search, filters, and a row per item. Clicking a row opens it. */
export function MaterialsList(props: Readonly<{ data: PriceListData; openUuid?: string; onOpen: (uuid: string) => void; onNew: () => void }>) {
    const { data } = props;
    const fmt = useMoney();
    // A link can open it already searched (e.g. "See all" from Home's search): ?q=...
    const [query, setQuery] = useState(() => new URLSearchParams(globalThis.location.search).get("q") ?? "");
    // Links (e.g. from Home) can open it on a filter: ?filter=check; or (a group's Items tab) on a group's items: ?inGroup=...
    // (not ?group=: that opens a group's window, which shouldn't filter the list).
    const linked = new URLSearchParams(useSearch()).get("filter");
    // The group it's narrowed to lives in the address (with any windows open): cleared there, keeping them.
    const windows = usePriceListWindows();
    const inGroup = data.pricingGroups.find((g) => g.id === windows.params.get("inGroup"));
    // Costs, markups and pricing (and the filters about them) only with See costs: the server leaves them out otherwise.
    const seesCosts = can(data, "costs.view");
    const showGroups = useMemo(() => filterGroups(Boolean(data.lastUsed), seesCosts), [data.lastUsed, seesCosts]);
    const [filter, setFilter] = useState<Filter>(() => (showGroups.some((g) => g.filters.includes(linked as Filter)) ? (linked as Filter) : "active"));
    const [shown, setShown] = useState(PAGE);

    const withSupplierPrices = useMemo(() => new Set(data.supplierPrices.map((p) => p.materialUuid)), [data.supplierPrices]);
    /** Every item, with what the list and its filters need: what needs attention is attention.ts's (as its window's and Home's). */
    const all = useMemo(() => {
        const ctx = itemContext(data);
        return data.materials.map((m) => {
            const { rate, tax } = itemTax(data, m);
            const pricing = data.itemPricing[m.uuid];
            const gaps = setupGaps(partItem(data, m), { hasSupplierPrice: withSupplierPrices.has(m.uuid), hasPricing: pricing !== undefined });
            return { m, rate, tax, pricing, gaps, ...assessItem(m, ctx) };
        });
    }, [data, withSupplierPrices]);
    /** How many items each filter shows (search aside), for the counts by each. */
    // Narrowed to a group: its items only (counts too). A group no longer there: everything.
    const scoped = useMemo(() => (inGroup ? all.filter((row) => row.pricing?.groupId === inGroup.id) : all), [all, inGroup]);
    const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.id, scoped.filter((row) => inFilter(f.id, row, data)).length])) as Record<Filter, number>, [scoped, data]);
    const groupNames = useMemo(() => new Map(data.pricingGroups.map((g) => [g.id, g.name])), [data.pricingGroups]);
    // Searched by name, item number, barcode and its group's name ("deadbolt" finds the Deadbolts group's items).
    const rows = useMemo(
        () =>
            scoped.filter(
                (row) => inFilter(filter, row, data) && matchesSearch(itemSearchText(row.m, groupNames.get(row.pricing?.groupId ?? "")), query),
            ),
        [scoped, data, query, filter, groupNames],
    );
    const pick = (f: Filter) => {
        setFilter(f);
        setShown(PAGE);
    };

    return (
        <div className="page is-wide">
            <PageHeader
                title="Materials"
                subtitle={`${data.materials.filter((m) => m.active).length.toLocaleString()} active items, synced with ServiceM8.`}
                actions={
                    can(data, "items.add") ? (
                        <button type="button" className="btn-primary" onClick={props.onNew}>
                            <Plus className="size-4" /> New item
                        </button>
                    ) : null
                }
            />
            {whyNot(data, "items.edit") ? (
                <div className="mb-4">
                    <Notice>{whyNot(data, "items.edit")}</Notice>
                </div>
            ) : null}
            {filter === "unknown" && can(data, "items.review") && rows.length > 0 ? <MarkAllKnown uuids={rows.map((r) => r.m.uuid)} /> : null}
            {(filter === "costUpdate" && can(data, "costs.edit") && rows.length > 0) || data.costsUpdating ? (
                <UpdateAllCosts uuids={filter === "costUpdate" ? rows.map((r) => r.m.uuid) : []} running={data.costsUpdating} />
            ) : null}
            <Panel flush>
                <ListToolbar
                    searchLabel="Search materials"
                    placeholder={seesCosts ? "Search by name, item number, barcode or group" : "Search by name, item number or barcode"}
                    query={query}
                    onQuery={(q) => {
                        setQuery(q);
                        setShown(PAGE);
                    }}
                    filter={filter}
                    onFilter={pick}
                    groups={showGroups}
                    labels={SHOW_LABELS}
                    counts={counts}
                />
                {inGroup ? (
                    <div className="flex items-center gap-2 border-b border-line px-4 py-2 text-sm">
                        <button type="button" className="inline-flex items-center gap-1.5 rounded-full bg-surface-2 px-2.5 py-0.5 text-ink" title="Show every item again" onClick={() => windows.set({ inGroup: null })}>
                            <Layers className="size-3.5 text-brand-ink" /> In the {inGroup.name} group <X className="size-3.5 text-muted" />
                        </button>
                    </div>
                ) : null}
                <table className={clsx("list is-fixed", seesCosts ? "min-w-[1000px]" : "min-w-[600px]")}>
                    {/* The item number and figures get just the room they need; the name gets the rest. */}
                    <colgroup>
                        <col className="w-28" />
                        <col />
                        <col className="w-32" />
                        {seesCosts ? (
                            <>
                                <col className="w-24" />
                                <col className="w-20" />
                                <col className="w-24" />
                                <col className="w-52" />
                            </>
                        ) : null}
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Item #</th>
                            <th>Name</th>
                            <th className="text-right">Price</th>
                            {seesCosts ? (
                                <>
                                    <th className="text-right">Cost</th>
                                    <th className="text-right">Markup</th>
                                    <th className="text-right" title="Estimated profit per unit before tax: its price less its cost (the part's, and its labour's)">
                                        Est. profit
                                    </th>
                                    <th>Pricing</th>
                                </>
                            ) : null}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.slice(0, shown).map(({ m, rate, tax, pricing, check, profit, numberIssue, problems }) => (
                            <MaterialLine
                                key={m.uuid}
                                m={m}
                                problems={problems}
                                numberIssue={numberIssue}
                                tags={data.nameTags}
                                basis={basisLabel(m.price_includes_taxes, rate)}
                                basisFull={basisLabel(m.price_includes_taxes, rate, true)}
                                cost={shownCost(data, m)}
                                costTo={m.uuid in data.costUpdates ? shownCost(data, { ...m, cost: estimatedCost(data, m) }).amount : null}
                                costCheck={pricesDueOf(itemContext(data), m.uuid)}
                                setBy={describePricingShort(pricing, { includesTax: m.price_includes_taxes, tax }, fmt)}
                                typed={!pricing || isPlain(pricing)}
                                group={pricing?.groupId ? { id: pricing.groupId, name: groupNames.get(pricing.groupId) ?? "A group", overrides: pricing.overridesGroup } : undefined}
                                check={check}
                                lock={priceLock(data, m.uuid)}
                                markupPct={profit.markupPct}
                                total={profit.partOnly ? null : profit.profitNet}
                                needsLabourCost={profit.profitNet !== null && profit.partOnly}
                                minMarkup={data.settings.minMarkup}
                                seesCosts={seesCosts}
                                open={props.openUuid === m.uuid}
                                onOpen={() => props.onOpen(m.uuid)}
                            />
                        ))}
                    </tbody>
                </table>
                {rows.length === 0 ? <p className="p-6 text-center text-sm text-muted">No items match.</p> : null}
                {rows.length > shown ? (
                    <div className="border-t border-line p-3 text-center">
                        <button type="button" className="link-button text-sm" onClick={() => setShown((n) => n + PAGE)}>
                            Show more ({(rows.length - shown).toLocaleString()} left)
                        </button>
                    </div>
                ) : null}
            </Panel>
        </div>
    );
}

function MaterialLine(
    props: Readonly<{
        m: MaterialRow;
        problems: ItemProblem[];
        numberIssue: ItemAssessment["numberIssue"];
        tags: NameTagConfig;
        basis: string;
        /** The same, with the rate named as ServiceM8 has it (on hover). */
        basisFull: string;
        /** ServiceM8's cost (as the Price column, ServiceM8's price). */
        cost: { amount: number; label: string; fullLabel: string };
        /** While it has a cost to update: the cost it'll have (pricing.ts estimatedCost, what its markup and profit are on), else null. */
        costTo: number | null;
        /** Its supplier prices to check (its cost follows them): said on hover, its cost marked. */
        costCheck: string | null;
        setBy: string;
        typed: boolean;
        /** Its group (`overrides`: it's in it, but priced its own way). */
        group?: { id: string; name: string; overrides: boolean };
        check: NeedsCheck | null;
        /** What locks its price, if anything (PriceLock.tsx). */
        lock: PriceLock | null;
        markupPct: number | null;
        /** What it makes on its cost (attention.ts profit); `needsLabourCost`: it has labour, but no labour cost is set to tell. */
        total: number | null;
        needsLabourCost: boolean;
        minMarkup: number;
        /** Show its cost, markup and pricing (See costs). */
        seesCosts: boolean;
        open: boolean;
        onOpen: () => void;
    }>,
) {
    const { m, problems } = props;
    const fmt = useMoney();
    const low = problems.some((p) => p.id === "low");
    return (
        // The name button covers the whole row (after:inset-0): click selects it, double-click opens the item.
        <tr className={clsx("relative", rowTone(problems), props.open ? "bg-brand-500/10" : "hover:bg-surface-2 focus-within:bg-brand-500/5")} aria-current={props.open || undefined}>
            {/* Every row is the same height (the name and a small line under it fit). */}
            <td className="h-14 py-2">
                {/* Its item number, and before it what locks its price (if anything): the record's, not the amount's. */}
                <span className="flex min-w-0 items-center gap-1">
                    <LockMark lock={props.lock} />
                    <span className="min-w-0">
                        <ItemNumberCell itemNumber={m.item_number} issue={props.numberIssue} />
                    </span>
                </span>
            </td>
            <td className="py-2">
                <button type="button" className="block w-full min-w-0 cursor-default text-left outline-none after:absolute after:inset-0" {...rowOpen(props.onOpen)}>
                    <NameCell m={m} tags={props.tags} problems={problems.map((p) => p.text)} danger={problems.some((p) => p.danger)} />
                </button>
            </td>
            {/* The amount on its own line (never cut short), and under it, small: how it's taxed. */}
            <td className="text-right">
                <div className="flex justify-end whitespace-nowrap">
                    {/* Its pricing gives a different price now: flagged, hover for the price it should be. */}
                    {props.check ? (
                        <ListWarning title={checkText(props.check, fmt.money(props.check.target))}>
                            <span className="tabular-nums">{fmt.money(m.price)}</span>
                        </ListWarning>
                    ) : (
                        <span className="tabular-nums">{fmt.money(m.price)}</span>
                    )}
                </div>
                <div className="flex min-w-0 justify-end">
                    <TaxLabel label={props.basis} full={props.basisFull} />
                </div>
            </td>
            {props.seesCosts ? <CostCells {...props} low={low} /> : null}
        </tr>
    );
}

/**
 * The ServiceM8 name on one line (the name and its tags in brackets, cut short if need be: the
 * whole name shows on hover), and an icon when its group's name tags read differently in
 * it (`nameGroup`). Under it, small: the lines ServiceM8 shows under the name (e.g. special order)
 * and the stock, when there are any.
 */
function NameCell({ m, tags, problems, danger }: Readonly<{ m: MaterialRow; tags: NameTagConfig; problems: string[]; danger: boolean }>) {
    const fmt = useMoney();
    const under = m.name.includes("\n");
    return (
        <>
            <span className="flex min-w-0 items-center gap-1.5" title={m.name}>
                <SyncBadge synced={m.synced} />
                <span className={clsx("min-w-0 truncate font-semibold", !m.active && "text-muted line-through")}>
                    <ServiceM8Name name={m.name} tags={tags} only="first" />
                </span>
                {/* Everything that needs attention, as one mark (a count when more than one): hover for the list; open the item to deal with them. */}
                <NeedsAttention problems={problems} danger={danger} />
            </span>
            {under || m.item_is_inventoried ? (
                <span className="mt-0.5 block truncate pl-5 text-xs text-muted">
                    {under ? <ServiceM8Name name={m.name} tags={tags} only="rest" /> : null}
                    {under && m.item_is_inventoried ? " · " : null}
                    {m.item_is_inventoried ? `${fmt.number(m.quantity_in_stock)} in stock` : null}
                </span>
            ) : null}
        </>
    );
}

/**
 * How its price is set: a typed price says Manual (grey); otherwise what sets it on top (its
 * group, which opens it, or its own pricing) with the rule in small text underneath. In a
 * group but overriding its price: the group, marked Overrides, and its own rule underneath.
 */
function PricingCell({ group, setBy, typed }: Readonly<{ group?: { id: string; name: string; overrides: boolean }; setBy: string; typed: boolean }>) {
    const windows = usePriceListWindows();
    if (!group && typed) return <div className="truncate text-muted" title="No pricing rule: the price is typed in">Typed price</div>;
    return (
        <>
            {group ? (
                // "relative" lifts it above the row's click area: a click opens the group, not the item.
                <button
                    type="button"
                    className="link-button relative flex max-w-full min-w-0 items-center gap-1 font-normal"
                    title={group.overrides ? `In the ${group.name} group, but overriding its price (priced its own way): open the group` : `Priced by the ${group.name} group: open it`}
                    onClick={() => windows.openGroup(group.id)}
                >
                    <Layers className="size-3.5 shrink-0" />
                    <span className="truncate">{group.name}</span>
                    {group.overrides ? <Badge tone="blue">Overrides</Badge> : null}
                </button>
            ) : (
                <div className="truncate">Own pricing</div>
            )}
            <div className="truncate text-xs text-muted" title={setBy}>
                {setBy}
            </div>
        </>
    );
}

/** A row that needs attention is tinted: red for a problem that's red (attention.ts), amber for anything else to sort out. */
function rowTone(problems: readonly ItemProblem[]) {
    if (problems.some((p) => p.danger)) return "is-danger";
    // Supplier prices to check aren't tinted (attention.ts tintsRow): marked by its cost.
    return problems.some(tintsRow) ? "is-warning" : null;
}

/** Its cost, markup and pricing (See costs). */
function CostCells(
    props: Readonly<{
        cost: { amount: number; label: string; fullLabel: string };
        costTo: number | null;
        costCheck: string | null;
        markupPct: number | null;
        low: boolean;
        total: number | null;
        needsLabourCost: boolean;
        minMarkup: number;
        group?: { id: string; name: string; overrides: boolean };
        setBy: string;
        typed: boolean;
    }>,
) {
    const fmt = useMoney();
    return (
        <>
            {/* As the price: the amount, and under it how it's taxed. */}
            <td className="text-right">
                <div className="flex justify-end whitespace-nowrap">
                    {/* A cost to update: ServiceM8's, flagged; hover for the cost it'll have (as a price to update is). */}
                    {/* Its supplier prices to check: marked too, both said when there's both. */}
                    {props.costTo === null && props.costCheck === null ? (
                        <span className="tabular-nums">{fmt.money(props.cost.amount)}</span>
                    ) : (
                        <ListWarning
                            title={[props.costTo === null ? null : `Cost to update: it'll be ${fmt.money(props.costTo)}. Its markup and profit are on that.`, props.costCheck, "Open the item to see."].filter(Boolean).join(" ")}
                        >
                            <span className="tabular-nums">{fmt.money(props.cost.amount)}</span>
                        </ListWarning>
                    )}
                </div>
                <div className="flex min-w-0 justify-end">
                    <TaxLabel label={props.cost.label} full={props.cost.fullLabel} />
                </div>
            </td>
            <td className="text-right tabular-nums">
                <MarkupCell markupPct={props.markupPct} low={props.low} minMarkup={props.minMarkup} />
            </td>
            <td className="text-right tabular-nums">
                <TotalCell total={props.total} needsLabourCost={props.needsLabourCost} />
            </td>
            <td>
                <PricingCell group={props.group} setBy={props.setBy} typed={props.typed} />
            </td>
        </>
    );
}

/** A tax label under an amount ("inc GST"): cut with "…" only when a long one doesn't fit the column, in full (as ServiceM8 names the rate) on hover. */
function TaxLabel({ label, full }: Readonly<{ label: string; full: string }>) {
    return (
        <span className="min-w-0 truncate text-xs text-muted" title={full}>
            {label}
        </span>
    );
}

/** Its estimated profit per unit before tax (its price less its cost: the part's and its labour's); "-" when that can't be told (no cost, or labour with no labour cost set). */
function TotalCell({ total, needsLabourCost }: Readonly<{ total: number | null; needsLabourCost: boolean }>) {
    const fmt = useMoney();
    if (total !== null) return <>{fmt.money(total)}</>;
    return (
        <span className="text-muted" title={needsLabourCost ? "It has labour: set a labour cost in Price List settings to see its estimated profit." : undefined}>
            -
        </span>
    );
}

/** Its markup on its cost (on the part alone while its labour can't be costed), flagged red when it's under the minimum (Settings). */
function MarkupCell({ markupPct, low, minMarkup }: Readonly<{ markupPct: number | null; low: boolean; minMarkup: number }>) {
    const fmt = useMoney();
    if (markupPct === null) return <span className="text-muted">-</span>;
    const shown = fmt.percent(Math.round(markupPct));
    if (!low) return <>{shown}</>;
    const why = minMarkup > 0 ? `under your ${minMarkup}% minimum` : "nothing, or a loss";
    return (
        <ListWarning tone="danger" title={`Makes too little on its cost: ${why} (Price List settings). Open the item to change its pricing.`}>
            {shown}
        </ListWarning>
    );
}

/**
 * The item number, or what needs doing about it in its place: none ("Missing"), or shared with
 * another item (the number, flagged). Its row is tinted too (is-warning): every item needs its own.
 */
function ItemNumberCell({ itemNumber, issue }: Readonly<{ itemNumber: string; issue: ItemAssessment["numberIssue"] }>) {
    if (!issue) {
        return (
            <span className="block truncate text-muted" title={itemNumber || undefined}>
                {itemNumber || "-"}
            </span>
        );
    }
    return issue === "missing" ? (
        <ListWarning title="Every item needs an item number. Open it to set one.">Missing</ListWarning>
    ) : (
        <ListWarning title={`Every item needs its own item number: another item also uses "${itemNumber}". Open it to change one.`}>{itemNumber}</ListWarning>
    );
}

/** Its supplier prices to check, in full (its cost follows them), or null. */
function pricesDueOf(ctx: ReturnType<typeof itemContext>, uuid: string) {
    const due = ctx.pricesDue.get(uuid);
    return due ? pricesDueDetail(due) : null;
}

/** Hover text for the needs-checking icon by the price. */
function checkText(check: NeedsCheck, target: string) {
    if (check.reason === "edited") return `Price needs checking: it was edited in ServiceM8, but its pricing gives ${target}. Open the item to reset or keep it.`;
    return `Price needs checking: its pricing now gives ${target}. Open the item to update the price.`;
}

/** The "Needs attention" filters: each shows the items with that problem (attention.ts). */
const PROBLEM_FILTERS: Partial<Record<Filter, ItemProblemId>> = {
    unknown: "unknown",
    lockedChanged: "lockedChanged",
    changed: "changed",
    check: "check",
    costUpdate: "costUpdate",
    pricesDue: "pricesDue",
    names: "names",
    low: "low",
    partLoss: "partLoss",
    noNumber: "noNumber",
    dupNumber: "sharedNumber",
    dupName: "sharedName",
};

/** Whether a row belongs in the list for this filter (search aside). */
function inFilter(filter: Filter, row: { m: MaterialRow; gaps: SetupGaps; problems: readonly ItemProblem[] }, data: PriceListData): boolean {
    const { m } = row;
    const problem = PROBLEM_FILTERS[filter];
    if (problem) return row.problems.some((p) => p.id === problem);
    switch (filter) {
        case "active":
            return m.active;
        case "archived":
            return !m.active;
        case "attention":
            return row.problems.length > 0;
        case "noSupplier":
        case "noCost":
        case "typed":
            return m.active && row.gaps[filter];
        case "unused":
            return m.active && data.lastUsed !== null && (data.lastUsed[m.uuid] ?? "") < yearStart();
        case "overrides":
            return Boolean(data.itemPricing[m.uuid]?.overridesGroup);
        case "locked":
            return priceLock(data, m.uuid) !== null;
        case "appOnly":
            return !m.synced;
        default:
            return true;
    }
}

/** The Show list: which items, then narrowing down in groups (counts by each). */
const SHOW_GROUPS: { label: string | null; filters: Filter[] }[] = [
    { label: null, filters: ["active", "archived", "all"] },
    { label: "Needs attention", filters: ["attention", "unknown", "lockedChanged", "changed", "check", "costUpdate", "pricesDue", "names", "low", "partLoss", "noNumber", "dupNumber", "dupName"] },
    { label: "Setting up", filters: ["noSupplier", "noCost", "typed"] },
    { label: "Kinds of item", filters: ["locked", "overrides", "appOnly"] },
];
/** Shown once jobs and job lines are read in full (before then, items not reached yet would look unused). */
const USE_GROUP: (typeof SHOW_GROUPS)[number] = { label: "Use on jobs", filters: ["unused"] };
/** Filters about costs and pricing: only with See costs (without it, the list has nothing to go on). */
const COST_FILTERS: ReadonlySet<Filter> = new Set(["check", "costUpdate", "pricesDue", "names", "low", "partLoss", "noSupplier", "noCost", "typed", "overrides"]);

/** The Show list's groups this person gets: use on jobs once read in full, cost filters with See costs. */
function filterGroups(withUse: boolean, seesCosts: boolean) {
    const groups = withUse ? [...SHOW_GROUPS, USE_GROUP] : SHOW_GROUPS;
    if (seesCosts) return groups;
    return groups.map((g) => ({ ...g, filters: g.filters.filter((f) => !COST_FILTERS.has(f)) })).filter((g) => g.filters.length > 0);
}
/** The Show list's labels ("Active" reads "Active items" there). */
const SHOW_LABELS = Object.fromEntries(FILTERS.map((f) => [f.id, f.id === "active" ? "Active items" : f.label])) as Record<Filter, string>;

/**
 * On the Cost to update filter: update every cost it shows (search included) to what its labour, supplier price or parts give,
 * after asking (useConfirm). In the background (there can be hundreds, each a ServiceM8 save): items
 * leave the list as each is saved. While it runs, it says so wherever the list is open.
 */
function UpdateAllCosts({ uuids, running }: Readonly<{ uuids: string[]; running: boolean }>) {
    const saver = usePriceListSaver();
    const confirm = useConfirm();
    const [error, setError] = useState<Error | null>(null);
    const update = async () => {
        const yes = await confirm({
            title: `Update ${plural(uuids.length, "cost")}?`,
            message: `${uuids.length === 1 ? "Its cost goes" : "Their costs go"} to what ${uuids.length === 1 ? "its" : "their"} labour, supplier price or parts give. Each is changed in ServiceM8, and leaves this list as it's saved.`,
            confirmLabel: `Update ${plural(uuids.length, "cost")}`,
        });
        if (!yes) return;
        setError(null);
        saver.updateCosts(uuids).catch((err: Error) => setError(err));
    };
    if (running) {
        return (
            <div className="mb-4">
                <Notice>Updating costs. Items leave this list as each is saved in ServiceM8.</Notice>
            </div>
        );
    }
    return (
        <div className="mb-4 space-y-2">
            <Notice
                actions={
                    <button type="button" className="btn-secondary" onClick={() => void update()}>
                        Update all
                    </button>
                }
            >
                <strong>Costs to update.</strong> Their labour, supplier prices or parts give other costs now. Costs never change by themselves: update each from its window, or all of them here.
            </Notice>
            <ErrorNotice error={error} />
        </div>
    );
}

/**
 * On the Unknown items filter: keep every item it shows (search included) in one go, after asking
 * (useConfirm: for day one, when every item is unknown, or after checking a batch). Mistakes are
 * archived one by one.
 */
function MarkAllKnown({ uuids }: Readonly<{ uuids: string[] }>) {
    const saver = usePriceListSaver();
    const confirm = useConfirm();
    const [error, setError] = useState<Error | null>(null);
    const mark = async () => {
        const yes = await confirm({
            title: `Keep ${plural(uuids.length, "item")}?`,
            message: "Every item shown is marked as a real item: they stop showing as unknown.",
            confirmLabel: `Keep ${plural(uuids.length, "item")}`,
        });
        if (!yes) return;
        setError(null);
        saver.markKnown(uuids).catch((err: Error) => setError(err));
    };
    return (
        <div className="mb-4 space-y-2">
            <Notice
                actions={
                    <button type="button" className="btn-secondary" onClick={() => void mark()}>
                        Keep all
                    </button>
                }
            >
                <strong>Unknown items from ServiceM8.</strong> Please review them, then keep or archive each (archive from its window).
                <span className="mt-0.5 block text-xs opacity-80">Unknown items can come from part numbers on jobs that don&apos;t match an item, or from your accounting software once invoices are approved.</span>
            </Notice>
            <ErrorNotice error={error} />
        </div>
    );
}
